/**
 * Time zones for the pickers.
 *
 * The ones at the top are the regions this business actually covers, kept in
 * that order deliberately — a picker sorted alphabetically buries India below
 * Antarctica. Everything else the browser knows is still offered underneath, so
 * nothing is unreachable.
 *
 * Labels lead with the place and the abbreviation people say out loud; the IANA
 * name is what gets stored, because it is the only form that survives daylight
 * saving. `UTC` is offered as GMT and is not the same choice as `Europe/London`
 * — a UK account on `UTC` would have its business hours an hour out all summer.
 */
export interface TimezoneOption {
  zone: string;
  label: string;
}

export const COMMON_TIMEZONES: TimezoneOption[] = [
  { zone: 'UTC', label: 'GMT / UTC' },
  { zone: 'Europe/London', label: 'United Kingdom — GMT / BST' },
  { zone: 'Europe/Berlin', label: 'Central Europe — CET / CEST' },
  { zone: 'America/New_York', label: 'US Eastern — EST / EDT' },
  { zone: 'America/Los_Angeles', label: 'US Pacific — PST / PDT' },
  { zone: 'Asia/Kolkata', label: 'India — IST' },
  { zone: 'Australia/Sydney', label: 'Australia, Sydney — AEST / AEDT' },
  { zone: 'Asia/Singapore', label: 'Singapore — SGT' },
  { zone: 'Asia/Tokyo', label: 'Japan — JST' },
  { zone: 'Asia/Shanghai', label: 'China — CST' },
];

const COMMON_ZONES = new Set(COMMON_TIMEZONES.map((t) => t.zone));

/**
 * Every zone the browser knows, minus the ones already listed above.
 *
 * `Intl.supportedValuesOf` is typed only from a later lib than this project
 * targets, hence the cast; where it is missing the common list stands alone.
 */
export function otherTimezones(): string[] {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
    .supportedValuesOf;
  if (typeof supported !== 'function') return [];
  try {
    return supported('timeZone').filter((zone) => !COMMON_ZONES.has(zone));
  } catch {
    return [];
  }
}

/** The wall clock in `zone` right now, or null if the zone is not one we know. */
export function localTimeIn(zone: string): string | null {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date());
  } catch {
    return null;
  }
}

/** "Drives business hours." plus what time it is there, when the zone is real. */
export function timezoneHint(zone: string, purpose: string): string {
  const now = localTimeIn(zone);
  return now ? `${purpose} It is ${now} there now.` : purpose;
}
